import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type FormEvent,
} from "react";
import type { EventClickArg } from "@fullcalendar/core";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import {
  createCalendar,
  createScheduledItem,
  deleteScheduledItem,
  getCalendars,
  getScheduledItems,
  type ScheduleCalendar,
  type ScheduledItem,
} from "../Features/Schedule/API/schedule";
import {
  ensureCurrentUser,
  getStoredCurrentUser,
  type CurrentUser,
} from "../Features/Users/userIdentity";

const timeZones = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "UTC",
  "Europe/London",
  "Europe/Paris",
  "Asia/Tokyo",
  "Australia/Sydney",
];

const calendarEventColor = "#3788d8";
const calendarEventBorderColor = "#2f76bf";
const calendarEventHoverColor = "#2f76bf";
const calendarEventHoverBorderColor = "#245f9b";

const timeOptions = Array.from({ length: (24 * 60) / 5 + 1 }, (_, index) => {
  const minute = index * 5;
  const cappedMinute = Math.min(minute, 1440);
  return {
    label: formatMinuteOfDay(cappedMinute),
    value: minuteToTimeValue(cappedMinute),
  };
});

export function Calendar() {
  const [calendars, setCalendars] = useState<ScheduleCalendar[]>([]);
  const [selectedCalendarId, setSelectedCalendarId] = useState("");
  const [scheduledItems, setScheduledItems] = useState<ScheduledItem[]>([]);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(() =>
    getStoredCurrentUser(),
  );
  const [calendarName, setCalendarName] = useState("");
  const [calendarTimeZone, setCalendarTimeZone] = useState(getBrowserTimeZone());
  const [itemTitle, setItemTitle] = useState("");
  const [itemDescription, setItemDescription] = useState("");
  const [itemDate, setItemDate] = useState(getTodayDateValue);
  const [itemStartTime, setItemStartTime] = useState(getDefaultStartTimeValue);
  const [itemEndTime, setItemEndTime] = useState(getDefaultEndTimeValue);
  const [selectedScheduledItemId, setSelectedScheduledItemId] = useState<
    string | null
  >(null);
  const [hoveredScheduledItemId, setHoveredScheduledItemId] = useState<
    string | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [savingCalendar, setSavingCalendar] = useState(false);
  const [savingItem, setSavingItem] = useState(false);
  const [deletingItem, setDeletingItem] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedCalendar = calendars.find(
    (calendar) => calendar.id === selectedCalendarId,
  );
  const selectedScheduledItem = scheduledItems.find(
    (item) => item.id === selectedScheduledItemId,
  );
  const userId = currentUser?.userId ?? "";

  const calendarEvents = useMemo(
    () =>
      scheduledItems.map((item) => ({
        id: item.id,
        title: item.title,
        start: toCalendarWallTime(item.startsAt, selectedCalendar?.timeZone),
        end: toCalendarWallTime(item.endsAt, selectedCalendar?.timeZone),
        backgroundColor:
          hoveredScheduledItemId === item.id
            ? calendarEventHoverColor
            : calendarEventColor,
        borderColor:
          hoveredScheduledItemId === item.id
            ? calendarEventHoverBorderColor
            : calendarEventBorderColor,
        extendedProps: {
          duration: formatDuration(item.localStartMinute, item.localEndMinute),
        },
      })),
    [hoveredScheduledItemId, scheduledItems, selectedCalendar?.timeZone],
  );

  const loadScheduledItems = useCallback(async (calendarId: string) => {
    const trimmedUserId = userId.trim();

    if (!trimmedUserId) {
      return;
    }

    setError(null);

    try {
      const loadedItems = await getScheduledItems(calendarId, trimmedUserId);
      setScheduledItems(loadedItems);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    }
  }, [userId]);

  const loadCalendars = useCallback(async () => {
    const trimmedUserId = userId.trim();

    if (!trimmedUserId) {
      setCalendars([]);
      setSelectedCalendarId("");
      setScheduledItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const loadedCalendars = await getCalendars(trimmedUserId);
      const nextCalendarId =
        loadedCalendars.find((calendar) => calendar.id === selectedCalendarId)
          ?.id ||
        loadedCalendars[0]?.id ||
        "";

      setCalendars(loadedCalendars);
      setSelectedCalendarId(nextCalendarId);

      if (nextCalendarId) {
        await loadScheduledItems(nextCalendarId);
      } else {
        setScheduledItems([]);
      }
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [loadScheduledItems, selectedCalendarId, userId]);

  useEffect(() => {
    ensureCurrentUser()
      .then(setCurrentUser)
      .catch((requestError) => {
        setError(getErrorMessage(requestError));
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    void Promise.resolve().then(loadCalendars);
  }, [currentUser, loadCalendars]);

  useEffect(() => {
    if (!selectedScheduledItem) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setSelectedScheduledItemId(null);
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedScheduledItem]);

  function handleRefresh() {
    loadCalendars();

    if (selectedCalendarId && userId.trim()) {
      loadScheduledItems(selectedCalendarId);
    }
  }

  async function handleCreateCalendar(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const trimmedName = calendarName.trim();

    if (!trimmedName || !userId.trim()) {
      return;
    }

    setSavingCalendar(true);
    setError(null);

    try {
      const createdCalendar = await createCalendar({
        userId: userId.trim(),
        name: trimmedName,
        timeZone: calendarTimeZone,
        calendarType: "Daily",
      });
      setCalendars((currentCalendars) => [...currentCalendars, createdCalendar]);
      setSelectedCalendarId(createdCalendar.id);
      setCalendarName("");
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setSavingCalendar(false);
    }
  }

  async function handleCreateScheduledItem(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (
      !selectedCalendar ||
      !userId.trim() ||
      !itemTitle.trim() ||
      !itemDate ||
      !itemStartTime ||
      !itemEndTime
    ) {
      return;
    }

    setSavingItem(true);
    setError(null);

    try {
      await createScheduledItem({
        userId: userId.trim(),
        calendarId: selectedCalendar.id,
        name: itemTitle.trim(),
        description: itemDescription.trim() || null,
        date: itemDate,
        startMinute: getMinuteFromTimeValue(itemStartTime),
        endMinute: getMinuteFromTimeValue(itemEndTime),
      });
      await loadScheduledItems(selectedCalendar.id);
      setItemTitle("");
      setItemDescription("");
      setItemDate(getTodayDateValue());
      setItemStartTime(getDefaultStartTimeValue());
      setItemEndTime(getDefaultEndTimeValue());
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setSavingItem(false);
    }
  }

  async function handleDeleteScheduledItem() {
    if (!selectedCalendar || !selectedScheduledItem || !userId.trim()) {
      return;
    }

    setDeletingItem(true);
    setError(null);

    try {
      await deleteScheduledItem(
        selectedCalendar.id,
        selectedScheduledItem.id,
        userId.trim(),
      );
      setScheduledItems((currentItems) =>
        currentItems.filter((item) => item.id !== selectedScheduledItem.id),
      );
      setSelectedScheduledItemId(null);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setDeletingItem(false);
    }
  }

  function handleEventClick(eventInfo: EventClickArg) {
    setSelectedScheduledItemId(eventInfo.event.id);
  }

  return (
    <main style={styles.page}>
      <header style={styles.header}>
        <div>
          <h1 style={styles.heading}>Calendar</h1>
          <p style={styles.subtle}>
            {selectedCalendar
              ? `${scheduledItems.length} scheduled items in ${selectedCalendar.timeZone}`
              : "Create a calendar to start scheduling"}
          </p>
        </div>
        <Button type="button" variant="outlined" onClick={handleRefresh}>
          Refresh
        </Button>
      </header>

      {error && <p style={styles.error}>{error}</p>}

      {selectedScheduledItem && (
        <div
          style={styles.modalOverlay}
          role="presentation"
          onClick={() => setSelectedScheduledItemId(null)}
        >
          <section
            style={styles.detailsPanel}
            role="dialog"
            aria-modal="true"
            aria-label="Scheduled item details"
            onClick={(event) => event.stopPropagation()}
          >
            <header style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{selectedScheduledItem.title}</h2>
              <Button
                type="button"
                onClick={() => setSelectedScheduledItemId(null)}
              >
                Close
              </Button>
            </header>

            <dl style={styles.detailsGrid}>
              <div>
                <dt style={styles.detailLabel}>Calendar</dt>
                <dd style={styles.detailValue}>
                  {selectedCalendar?.name ?? selectedScheduledItem.calendarId}
                </dd>
              </div>
              <div>
                <dt style={styles.detailLabel}>Date</dt>
                <dd style={styles.detailValue}>
                  {formatLocalDate(selectedScheduledItem.localDate)}
                </dd>
              </div>
              <div>
                <dt style={styles.detailLabel}>Start</dt>
                <dd style={styles.detailValue}>
                  {formatMinuteOfDay(selectedScheduledItem.localStartMinute)}
                </dd>
              </div>
              <div>
                <dt style={styles.detailLabel}>End</dt>
                <dd style={styles.detailValue}>
                  {formatMinuteOfDay(selectedScheduledItem.localEndMinute)}
                </dd>
              </div>
              <div>
                <dt style={styles.detailLabel}>Duration</dt>
                <dd style={styles.detailValue}>
                  {formatDuration(
                    selectedScheduledItem.localStartMinute,
                    selectedScheduledItem.localEndMinute,
                  )}
                </dd>
              </div>
              <div>
                <dt style={styles.detailLabel}>Timezone</dt>
                <dd style={styles.detailValue}>
                  {selectedCalendar?.timeZone ?? "UTC"}
                </dd>
              </div>
              <div>
                <dt style={styles.detailLabel}>UTC Start</dt>
                <dd style={styles.detailValue}>
                  {formatUtcInstant(selectedScheduledItem.startsAt)}
                </dd>
              </div>
              <div>
                <dt style={styles.detailLabel}>UTC End</dt>
                <dd style={styles.detailValue}>
                  {formatUtcInstant(selectedScheduledItem.endsAt)}
                </dd>
              </div>
              <div style={styles.detailWide}>
                <dt style={styles.detailLabel}>Description</dt>
                <dd style={styles.detailValue}>
                  {selectedScheduledItem.description || "No description."}
                </dd>
              </div>
              <div style={styles.detailWide}>
                <dt style={styles.detailLabel}>Scheduled Item ID</dt>
                <dd style={styles.monoValue}>{selectedScheduledItem.id}</dd>
              </div>
            </dl>

            <footer style={styles.modalActions}>
              <Button
                type="button"
                color="error"
                variant="contained"
                onClick={handleDeleteScheduledItem}
                disabled={deletingItem}
              >
                {deletingItem ? "Deleting..." : "Delete scheduled item"}
              </Button>
            </footer>
          </section>
        </div>
      )}

      <section style={styles.layout}>
        <aside style={styles.sidebar} aria-label="Calendar controls">
          <form onSubmit={handleCreateCalendar} style={styles.form}>
            <h2 style={styles.sectionTitle}>New Calendar</h2>
            <Stack sx={{ gap: 2 }}>
              <TextField
                label="Calendar name"
                value={calendarName}
                onChange={(event) => setCalendarName(event.target.value)}
                required
                fullWidth
                sx={textFieldSx}
              />
              <TextField
                select
                label="Timezone"
                value={calendarTimeZone}
                onChange={(event) => setCalendarTimeZone(event.target.value)}
                fullWidth
                sx={textFieldSx}
              >
                {timeZones.map((timeZone) => (
                  <MenuItem key={timeZone} value={timeZone}>
                    {timeZone}
                  </MenuItem>
                ))}
              </TextField>
              <Button type="submit" variant="contained" disabled={savingCalendar}>
                {savingCalendar ? "Creating..." : "Create calendar"}
              </Button>
            </Stack>
          </form>

          <form onSubmit={handleCreateScheduledItem} style={styles.form}>
            <h2 style={styles.sectionTitle}>Scheduled Item</h2>
            <Stack sx={{ gap: 2 }}>
              <TextField
                select
                label="Calendar"
                value={selectedCalendarId}
                onChange={(event) => {
                  const calendarId = event.target.value;
                  setSelectedCalendarId(calendarId);

                  if (userId.trim()) {
                    loadScheduledItems(calendarId);
                  }
                }}
                disabled={calendars.length === 0}
                fullWidth
                sx={textFieldSx}
              >
                {calendars.map((calendar) => (
                  <MenuItem key={calendar.id} value={calendar.id}>
                    {calendar.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Title"
                value={itemTitle}
                onChange={(event) => setItemTitle(event.target.value)}
                disabled={!selectedCalendar}
                required
                fullWidth
                sx={textFieldSx}
              />
              <TextField
                label="Description"
                value={itemDescription}
                onChange={(event) => setItemDescription(event.target.value)}
                disabled={!selectedCalendar}
                multiline
                minRows={2}
                fullWidth
                sx={textFieldSx}
              />
              <TextField
                type="date"
                label="Date"
                value={itemDate}
                onChange={(event) => setItemDate(event.target.value)}
                disabled={!selectedCalendar}
                required
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
                sx={textFieldSx}
              />
              <Stack direction={{ xs: "column", sm: "row" }} sx={{ gap: 2 }}>
                <TextField
                  select
                  label="Start"
                  value={itemStartTime}
                  onChange={(event) => {
                    const startTime = event.target.value;
                    setItemStartTime(startTime);

                    if (
                      getMinuteFromTimeValue(itemEndTime) <=
                      getMinuteFromTimeValue(startTime)
                    ) {
                      setItemEndTime(
                        minuteToTimeValue(
                          Math.min(getMinuteFromTimeValue(startTime) + 30, 1440),
                        ),
                      );
                    }
                  }}
                  disabled={!selectedCalendar}
                  required
                  fullWidth
                  sx={textFieldSx}
                >
                  {timeOptions.slice(0, -1).map((timeOption) => (
                    <MenuItem key={timeOption.value} value={timeOption.value}>
                      {timeOption.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="End"
                  value={itemEndTime}
                  onChange={(event) => setItemEndTime(event.target.value)}
                  disabled={!selectedCalendar}
                  required
                  fullWidth
                  sx={textFieldSx}
                >
                  {timeOptions
                    .filter(
                      (timeOption) =>
                        getMinuteFromTimeValue(timeOption.value) >
                        getMinuteFromTimeValue(itemStartTime),
                    )
                    .map((timeOption) => (
                      <MenuItem key={timeOption.value} value={timeOption.value}>
                        {timeOption.label}
                      </MenuItem>
                    ))}
                </TextField>
              </Stack>
              <Button
                type="submit"
                variant="contained"
                disabled={!selectedCalendar || !userId.trim() || savingItem}
              >
                {savingItem ? "Saving..." : "Save scheduled item"}
              </Button>
            </Stack>
          </form>
        </aside>

        <section style={styles.calendarPanel} aria-label="Scheduled items">
          {loading ? (
            <p style={styles.status}>Loading calendars...</p>
          ) : selectedCalendar ? (
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
              initialView="timeGridDay"
              events={calendarEvents}
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "timeGridDay,timeGridWeek,dayGridMonth,listMonth",
              }}
              views={{
                timeGridDay: {
                  buttonText: "Day",
                  slotMinTime: "00:00:00",
                  slotMaxTime: "24:00:00",
                },
                timeGridWeek: {
                  buttonText: "Week",
                },
                dayGridMonth: {
                  buttonText: "Month",
                },
                listMonth: {
                  buttonText: "List",
                  noEventsText: "No scheduled items in this range",
                },
              }}
              allDaySlot={false}
              eventContent={(eventInfo) => (
                <div
                  style={{
                    ...styles.eventContent,
                    ...(hoveredScheduledItemId === eventInfo.event.id
                      ? styles.eventContentHovered
                      : {}),
                  }}
                >
                  <span style={styles.eventTime}>
                    {eventInfo.timeText} &nbsp;&nbsp;&nbsp; Length:{" "}
                    {eventInfo.event.extendedProps.duration}
                  </span>
                  <span style={styles.eventTitle}>{eventInfo.event.title}</span>
                </div>
              )}
              eventClick={handleEventClick}
              eventMouseEnter={(eventInfo) =>
                setHoveredScheduledItemId(eventInfo.event.id)
              }
              eventMouseLeave={() => setHoveredScheduledItemId(null)}
              eventMinHeight={34}
              eventShortHeight={34}
              eventOrder="start,duration,title"
              expandRows
              height={760}
              slotDuration="00:05:00"
              slotLabelInterval="00:30:00"
              slotEventOverlap={false}
              timeZone="local"
              nowIndicator
            />
          ) : (
            <p style={styles.status}>No calendars yet.</p>
          )}
        </section>
      </section>
    </main>
  );
}

function getBrowserTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

function getDefaultStartDateTime() {
  const date = new Date();
  date.setMinutes(0, 0, 0);
  date.setHours(date.getHours() + 1);
  return toDateTimeLocalValue(date);
}

function getTodayDateValue() {
  return toDateTimeLocalValue(new Date()).slice(0, 10);
}

function getDefaultStartTimeValue() {
  return getDefaultStartDateTime().slice(11, 16);
}

function getDefaultEndTimeValue() {
  const date = new Date();
  date.setMinutes(0, 0, 0);
  date.setHours(date.getHours() + 2);
  return toDateTimeLocalValue(date).slice(11, 16);
}

function toDateTimeLocalValue(date: Date) {
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
}

function formatDuration(startMinute: number, endMinute: number) {
  const duration = endMinute - startMinute;
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;

  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  }

  if (hours > 0) {
    return `${hours}h`;
  }

  return `${minutes}m`;
}

function toCalendarWallTime(instant: string, timeZone?: string) {
  if (!timeZone || !instant) {
    return instant;
  }

  const date = new Date(instant);

  if (Number.isNaN(date.getTime())) {
    return instant;
  }

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const values = Object.fromEntries(
    parts
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );

  return `${values.year}-${values.month}-${values.day}T${values.hour}:${values.minute}:${values.second}`;
}

function getMinuteFromTimeValue(timeValue: string) {
  if (timeValue === "24:00") {
    return 1440;
  }

  const [hours, minutes] = timeValue.split(":").map(Number);
  return hours * 60 + minutes;
}

function minuteToTimeValue(minute: number) {
  if (minute === 1440) {
    return "24:00";
  }

  return `${Math.floor(minute / 60).toString().padStart(2, "0")}:${(minute % 60)
    .toString()
    .padStart(2, "0")}`;
}

function formatMinuteOfDay(minute: number) {
  if (minute === 1440) {
    return "12:00 AM next day";
  }

  const hours = Math.floor(minute / 60);
  const minutes = minute % 60;
  const period = hours >= 12 ? "PM" : "AM";
  const displayHour = hours % 12 || 12;

  return `${displayHour}:${minutes.toString().padStart(2, "0")} ${period}`;
}

function formatLocalDate(date: string) {
  const parsedDate = new Date(`${date}T00:00:00`);

  if (Number.isNaN(parsedDate.getTime())) {
    return date;
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "full",
  }).format(parsedDate);
}

function formatUtcInstant(instant: string) {
  const date = new Date(instant);

  if (Number.isNaN(date.getTime())) {
    return instant;
  }

  return new Intl.DateTimeFormat(undefined, {
    timeZone: "UTC",
    timeZoneName: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Schedule request failed";
}

const styles = {
  page: {
    width: "min(1180px, calc(100vw - 32px))",
    margin: "0 auto",
    padding: "28px 0",
    textAlign: "left",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "16px",
  },
  heading: {
    margin: 0,
  },
  subtle: {
    marginTop: "4px",
    color: "var(--text)",
    fontSize: "14px",
  },
  error: {
    border: "1px solid #ef4444",
    borderRadius: "8px",
    background: "rgba(239, 68, 68, 0.1)",
    color: "#ef4444",
    padding: "10px 12px",
    marginBottom: "16px",
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 40,
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    background: "rgba(0, 0, 0, 0.42)",
    padding: "72px 16px 16px",
    overflowY: "auto",
  },
  detailsPanel: {
    width: "min(760px, 100%)",
    border: "1px solid var(--border)",
    borderRadius: "8px",
    background: "var(--bg)",
    boxShadow: "var(--shadow)",
    padding: "16px",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "16px",
  },
  modalTitle: {
    margin: 0,
    color: "var(--text-h)",
    fontSize: "22px",
    fontWeight: 500,
  },
  detailsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "14px",
    margin: 0,
  },
  detailWide: {
    gridColumn: "1 / -1",
  },
  detailLabel: {
    color: "var(--text)",
    fontSize: "12px",
    textTransform: "uppercase",
  },
  detailValue: {
    margin: "4px 0 0",
    color: "var(--text-h)",
    overflowWrap: "anywhere",
  },
  monoValue: {
    margin: "4px 0 0",
    color: "var(--text-h)",
    fontFamily: "var(--mono)",
    fontSize: "13px",
    overflowWrap: "anywhere",
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "18px",
  },
  layout: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))",
    gap: "18px",
    alignItems: "start",
  },
  sidebar: {
    display: "grid",
    gap: "16px",
  },
  form: {
    border: "1px solid var(--border)",
    borderRadius: "8px",
    padding: "16px",
    background: "var(--bg)",
  },
  sectionTitle: {
    margin: "0 0 14px",
    fontSize: "18px",
  },
  calendarPanel: {
    minWidth: 0,
    border: "1px solid var(--border)",
    borderRadius: "8px",
    padding: "14px",
    background: "var(--bg)",
  },
  status: {
    padding: "28px",
    color: "var(--text)",
  },
  eventContent: {
    display: "grid",
    gap: "2px",
    minWidth: 0,
    height: "100%",
    borderRadius: "4px",
    padding: "2px 4px",
    cursor: "pointer",
    transition: "background-color 140ms ease, filter 140ms ease",
  },
  eventContentHovered: {
    background: "rgba(255, 255, 255, 0.18)",
    filter: "brightness(1.08)",
  },
  eventTime: {
    fontSize: "12px",
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  eventTitle: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  eventDuration: {
    fontSize: "12px",
    opacity: 0.8,
  },
} satisfies Record<string, CSSProperties>;

const textFieldSx = {
  "& .MuiInputBase-input": {
    color: "var(--text-h)",
  },
  "& .MuiInputBase-root": {
    color: "var(--text-h)",
  },
  "& .MuiInputLabel-root": {
    color: "var(--text)",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--border)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--accent-border)",
  },
};
